import { PolicySummaryDto } from '@src/dtos/policy-summary.dto';
import { List } from './list-display';
import { ApplicationsDefaultBehavior, LevelName } from '@src/utils/enums';
import { toCamelCase } from '@src/utils/string-normalization';

export interface PolicySummaryPanelData {
    headers: string[];
    lists: List[];
    summaries: string[];
}

export const makePluralOrSingular = (singularText: string, pluralText: string, count: number) => {
    return `${count} ${count !== 1 ? pluralText : singularText}`;
};

export const getGeoFilteringSummary = (data: PolicySummaryDto): PolicySummaryPanelData => {
    const { geoFilteringSummaryDto: geoSummary } = data;

    const blockedInboundHeader = `${geoSummary.blockedCountriesInbound.length} Blocked Inbound`;
    const blockedOutboundHeader = `${geoSummary.blockedCountriesOutbound.length} Blocked Outbound`;

    return {
        headers: [blockedInboundHeader, blockedOutboundHeader],
        lists: [
            {
                header: blockedInboundHeader,
                data: geoSummary.blockedCountriesInbound
            },
            {
                header: blockedOutboundHeader,
                data: geoSummary.blockedCountriesOutbound
            }
        ],
        summaries: []
    };
};

export const getWebFilteringSummary = (data: PolicySummaryDto): PolicySummaryPanelData => {
    const { webFilteringSummaryDto: webSummary } = data;

    const blockedCategoriesHeader = makePluralOrSingular('Category Blocked', 'Categories Blocked', webSummary.blockedWebCategories.length);
    const blockedDomainsHeader = makePluralOrSingular('Domain Blocked', 'Domains Blocked', webSummary.blockedDomains.length);
    const allowedDomainsHeader = makePluralOrSingular('Domain Allowed', 'Domains Allowed', webSummary.allowedDomains.length);

    return {
        headers: [blockedCategoriesHeader, blockedDomainsHeader, allowedDomainsHeader],
        lists: [
            {
                header: blockedCategoriesHeader,
                data: webSummary.blockedWebCategories
            },
            {
                header: blockedDomainsHeader,
                data: webSummary.blockedDomains
            },
            {
                header: allowedDomainsHeader,
                data: webSummary.allowedDomains
            }
        ],
        summaries: []
    };
};

export const getAppsSummary = (data: PolicySummaryDto): PolicySummaryPanelData  => {
    const { appsSummaryDto: appsSummary } = data;
    const convertDefaultBehavior = (defaultBehavior: ApplicationsDefaultBehavior) => {
        return defaultBehavior === ApplicationsDefaultBehavior.BLOCK_ALL ? 'Blocked' : 'Allowed';
    };
    const convertOppositeDefaultBehavior = (defaultBehavior: ApplicationsDefaultBehavior) => {
        return defaultBehavior === ApplicationsDefaultBehavior.BLOCK_ALL ? 'Allowed' : 'Blocked';
    };

    const defaultBehaviorHeader = `Default behavior: ${convertDefaultBehavior(appsSummary.appsDefaultBehaviorInbound)} Inbound,
     ${convertDefaultBehavior(appsSummary.appsDefaultBehaviorOutbound)} Outbound`;
    const inboundHeader = `${appsSummary.appsOppositeBehaviorInbound.length} ${convertOppositeDefaultBehavior(appsSummary.appsDefaultBehaviorInbound)} Inbound`;
    const outboundHeader = `${appsSummary.appsOppositeBehaviorOutbound.length} ${convertOppositeDefaultBehavior(appsSummary.appsDefaultBehaviorOutbound)} Outbound`;
    return {
        headers: [defaultBehaviorHeader, inboundHeader, outboundHeader],
        lists: [
            {
                header: inboundHeader,
                data: appsSummary.appsOppositeBehaviorInbound
            },
            {
                header: outboundHeader,
                data: appsSummary.appsOppositeBehaviorOutbound
            }
        ],
        summaries: [defaultBehaviorHeader]
    };
};

export const getServicesSummary = (data: PolicySummaryDto): PolicySummaryPanelData => {
    const { servicesSummaryDto: servicesSummary } = data;
    const blockedInbound = `${servicesSummary.blockedServicesInbound.length} Blocked Inbound`;
    const blockedOutbound = `${servicesSummary.blockedServicesOutbound.length} Blocked Outbound`;
    const allowedInbound = `${servicesSummary.allowedServicesInbound.length} Allowed Inbound`;
    const allowedOutbound = `${servicesSummary.allowedServicesOutbound.length} Allowed Outbound`;
    return {
        headers: [blockedInbound, blockedOutbound, allowedInbound, allowedOutbound],
        lists: [
        {
            header: blockedInbound,
            data: servicesSummary.blockedServicesInbound
        },
        {
            header: blockedOutbound,
            data: servicesSummary.blockedServicesOutbound
        },
        {
            header: allowedInbound,
            data: servicesSummary.allowedServicesInbound
        },
        {
            header: allowedOutbound,
            data: servicesSummary.allowedServicesOutbound
        }
    ],
        summaries: []
    };
};

export const getIpControlSummary = (data: PolicySummaryDto): PolicySummaryPanelData => {
    const { ipControlSummaryDto: ipControlSummary } = data;
    const blockedIpsHeader = `${ipControlSummary.blockedIPs.length} Blocked`;
    const allowedIpsHeader = `${ipControlSummary.allowedIPs.length} Allowed`;
    return {
        headers: [blockedIpsHeader, allowedIpsHeader],
        lists: [{
            header: blockedIpsHeader,
            data: ipControlSummary.blockedIPs
        },
            {
                header: allowedIpsHeader,
                data: ipControlSummary.allowedIPs
            }
        ],
        summaries: []
    };
};

export const getFirewallRulesSummary = (data: PolicySummaryDto): PolicySummaryPanelData => {
    const { firewallRulesSummaryDto: firewallRulesSummary } = data;
    const defaultRulesHeader = `${firewallRulesSummary.defaultRules.length} Default Rules`;
    const customRulesHeaders = `${makePluralOrSingular('Custom Rule', 'Custom Rules', firewallRulesSummary.customRules.length)}`;
    return {
        headers: [defaultRulesHeader, customRulesHeaders],
        lists: [
            {
                header: defaultRulesHeader,
                data: firewallRulesSummary.defaultRules
            },
            {
                header: customRulesHeaders,
                data: firewallRulesSummary.customRules
            }
        ],
        summaries: []
    };
};

export const getLibrarySummary = (data: PolicySummaryDto): PolicySummaryPanelData => {
    const { librarySummaryDto } = data;
    const addressesHeader = `${makePluralOrSingular('Address', 'Addresses', librarySummaryDto.addresses.length)}`;
    const addressGroupsHeader = `${makePluralOrSingular('Address Group', 'Address Groups', librarySummaryDto.addressGroups.length)}`;
    const servicesHeader = `${makePluralOrSingular('Service', 'Services', librarySummaryDto.services.length)}`;
    return {
        headers: [addressesHeader, addressGroupsHeader, servicesHeader],
        lists:
            [
                {
                    header: addressesHeader,
                    data: librarySummaryDto.addresses
                },
                {
                    header: addressGroupsHeader,
                    data: librarySummaryDto.addressGroups
                },
                {
                    header: servicesHeader,
                    data: librarySummaryDto.services
                }
            ],
        summaries: []
    };
};

export const getThreatProtectionSummary = (data: PolicySummaryDto, serviceLevel?: LevelName): PolicySummaryPanelData => {

    const { threatProtectionSummaryDto: threatProtectionSummary } = data;
    const shieldStatusHeader = `Dynamic Defense Shield is ${threatProtectionSummary.shieldStatus ? toCamelCase(threatProtectionSummary.shieldStatus) : 'Active'}`;

    const states = [threatProtectionSummary.virusState, threatProtectionSummary.spywareState, threatProtectionSummary.dnsState, threatProtectionSummary.vulnerabilityState];
    const alertCount = states.filter(state => state === 'ALERT').length;
    const blockCount = states.filter(state => state === 'BLOCK').length;
    const alertText = `${makePluralOrSingular('Protection is', 'Protections are', alertCount)} set to Alert`;
    const blockText = `${makePluralOrSingular('Protection is', 'Protections are', blockCount)} set to Block`;
    return {
        headers: [shieldStatusHeader, ...(serviceLevel === LevelName.GOLD ? [alertText, blockText] : [])],
        lists: [],
        summaries: [shieldStatusHeader,
            `DNS Protection is set to ${toCamelCase(threatProtectionSummary.dnsState)}`,
            ...(serviceLevel === LevelName.GOLD ?
                [`Virus Protection is set to ${toCamelCase(threatProtectionSummary.virusState)}`,
                    `Spyware Protection is set to ${toCamelCase(threatProtectionSummary.spywareState)}`,
                    `Vulnerability Protection is set to ${toCamelCase(threatProtectionSummary.vulnerabilityState)}`] : [])]
    };
};